import React from 'react';
import axios from 'axios';

const PersonDetailPopover = ({ 
  person, 
  onClose, 
  sheetData,
  API_URL,
  startApolloJob,
  getApolloJobStatus,
  fetchSheetData
}) => {
  if (!person) return null;

  // Always get the latest data from sheetData based on the person's ID or LinkedIn URL
  const personId = person.id || person.linkedin_url || `${person.name}-${person.current_employer}`;
  const latestPersonData = sheetData.find(p => {
    const pId = p.id || p.linkedin_url || `${p.name}-${p.current_employer}`;
    return pId === personId;
  }) || person; // Fallback to original person if not found

  const handleRocketReachContact = async () => {
    try {
      // Using environment variable for API URL - RocketReach
      const response = await axios.post(`${API_URL}/api/get-contact`, {
        linkedin_url: latestPersonData.linkedin_url,
        id: latestPersonData.id || '',
        timestamp: new Date().toISOString()
      });
      console.log('Get Contact (RocketReach) response:', response.data);
      // Refetch Google Sheets data after successful API call
      console.log('Refreshing sheet data after RocketReach contact retrieval...');
      await fetchSheetData();
      console.log('Sheet data refreshed successfully');
    } catch (error) {
      console.error('Error calling get-contact:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-yellow-500/20 backdrop-blur-sm">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold mb-2">
                {latestPersonData.name || latestPersonData.full_name || 'Unknown Name'}
              </h3>
              <p className="text-yellow-200 text-lg font-semibold">
                {latestPersonData.current_title || latestPersonData.title || 'No title available'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-black hover:text-gray-800 transition-colors duration-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 text-white">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {latestPersonData.linkedin_url && (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center border border-yellow-500/30">
                  <span className="text-yellow-400 font-bold">in</span>
                </div>
                <div>
                  <p className="text-sm text-gray-300">LinkedIn</p>
                  <a
                    href={latestPersonData.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-400 hover:text-yellow-300 transition-colors duration-200 text-sm truncate"
                  >
                    View Profile
                  </a>
                </div>
              </div>
            )}

            {(latestPersonData.location || latestPersonData.country) && (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center border border-yellow-500/30">
                  <span className="text-yellow-400">📍</span>
                </div>
                <div>
                  <p className="text-sm text-gray-300">Location</p>
                  <p className="text-sm font-medium text-white">
                    {[latestPersonData.location, latestPersonData.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Employment Info */}
          {(latestPersonData.current_employer || latestPersonData.current_employer_website) && (
            <div className="bg-black/30 rounded-xl p-4 border border-yellow-500/10">
              <h4 className="font-semibold text-yellow-400 mb-3">Current Employment</h4>
              <div className="space-y-2">
                {latestPersonData.current_employer && (
                  <div>
                    <p className="text-sm text-gray-300">Company</p>
                    <p className="font-medium text-white">{latestPersonData.current_employer}</p>
                  </div>
                )}
                {latestPersonData.current_employer_website && (
                  <div>
                    <p className="text-sm text-gray-300">Website</p>
                    <a
                      href={latestPersonData.current_employer_website.startsWith('http') 
                        ? latestPersonData.current_employer_website 
                        : `https://${latestPersonData.current_employer_website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-yellow-400 hover:text-yellow-300 transition-colors duration-200 text-sm"
                    >
                      {latestPersonData.current_employer_website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact Info */}
          {(latestPersonData.emails || latestPersonData.phones || latestPersonData['emails (A)'] || latestPersonData['phones (A)']) && (
            <div className="bg-black/30 rounded-xl p-4 border border-yellow-500/10">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-yellow-400">Contact Information</h4>
                {latestPersonData.linkedin_url && (
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={handleRocketReachContact}
                      className="px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-400 hover:to-blue-500 transition-all duration-200 font-medium text-xs shadow-lg shadow-blue-500/30"
                    >
                      Get Contact (R)
                    </button>
                    <button
                      onClick={() => startApolloJob(latestPersonData)}
                      disabled={getApolloJobStatus(latestPersonData).status === 'pending'}
                      className={`px-3 py-2 rounded-lg transition-all duration-200 font-medium text-xs shadow-lg relative ${
                        getApolloJobStatus(latestPersonData).status === 'pending'
                          ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black cursor-not-allowed'
                          : getApolloJobStatus(latestPersonData).status === 'completed'
                          ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-400 hover:to-green-500 shadow-green-500/30'
                          : getApolloJobStatus(latestPersonData).status === 'error'
                          ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-400 hover:to-red-500 shadow-red-500/30'
                          : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-400 hover:to-purple-500 shadow-purple-500/30'
                      }`}
                      title={
                        getApolloJobStatus(latestPersonData).status === 'pending'
                          ? `Processing... (${getApolloJobStatus(latestPersonData).estimatedTime || '1-2 min'})`
                          : getApolloJobStatus(latestPersonData).status === 'completed'
                          ? 'Apollo contact search completed! Click to run again.'
                          : getApolloJobStatus(latestPersonData).status === 'error'
                          ? `Error: ${getApolloJobStatus(latestPersonData).error}. Click to retry.`
                          : 'Start Apollo contact search'
                      }
                    >
                      {getApolloJobStatus(latestPersonData).status === 'pending' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <span className={getApolloJobStatus(latestPersonData).status === 'pending' ? 'opacity-0' : 'opacity-100'}>
                        {getApolloJobStatus(latestPersonData).status === 'completed' 
                          ? '✓ Contact (A)' 
                          : getApolloJobStatus(latestPersonData).status === 'error'
                          ? '⚠ Contact (A)'
                          : 'Get Contact (A)'}
                      </span>
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {/* RocketReach Email */}
                {latestPersonData.emails && (
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                      <span className="text-blue-400 text-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="inline h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor" className="text-blue-400" />
                        <path d="M3 7l9 6 9-6" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-300">Email (RocketReach)</p>
                      <div className="flex flex-wrap gap-2">
                        {latestPersonData.emails.split(',').map((email, index) => (
                          <a
                            key={index}
                            href={`mailto:${email.trim()}`}
                            className="text-blue-400 hover:text-blue-300 transition-colors duration-200 text-sm bg-black/30 px-2 py-1 rounded border border-blue-500/20"
                          >
                            {email.trim()}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Apollo Email */}
                {latestPersonData['emails (A)'] && (
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                      <span className="text-purple-400 text-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="inline h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor" className="text-purple-400" />
                        <path d="M3 7l9 6 9-6" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-300">Email (Apollo.io)</p>
                      <div className="flex flex-wrap gap-2">
                        {latestPersonData['emails (A)'].split(',').map((email, index) => (
                          <a
                            key={index}
                            href={`mailto:${email.trim()}`}
                            className="text-purple-400 hover:text-purple-300 transition-colors duration-200 text-sm bg-black/30 px-2 py-1 rounded border border-purple-500/20"
                          >
                            {email.trim()}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* RocketReach Phone */}
                {latestPersonData.phones && (
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                      <span className="text-blue-400 text-sm">📞</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-300">Phone (RocketReach)</p>
                      <div className="flex flex-wrap gap-2">
                        {latestPersonData.phones.split(',').map((phone, index) => (
                          <a
                            key={index}
                            href={`tel:${phone.trim()}`}
                            className="text-blue-400 hover:text-blue-300 transition-colors duration-200 text-sm bg-black/30 px-2 py-1 rounded border border-blue-500/20"
                          >
                            {phone.trim()}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Apollo Phone */}
                {latestPersonData['phones (A)'] && (
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                      <span className="text-purple-400 text-sm">📞</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-300">Phone (Apollo.io)</p>
                      <div className="flex flex-wrap gap-2">
                        {latestPersonData['phones (A)'].split(',').map((phone, index) => (
                          <a
                            key={index}
                            href={`tel:${phone.trim()}`}
                            className="text-purple-400 hover:text-purple-300 transition-colors duration-200 text-sm bg-black/30 px-2 py-1 rounded border border-purple-500/20"
                          >
                            {phone.trim()}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* All Data Section */}
          <div className="bg-black/30 rounded-xl p-4 border border-yellow-500/10">
            <h4 className="font-semibold text-yellow-400 mb-3">All Available Data</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              {Object.entries(latestPersonData).map(([key, value]) => {
                if (value && value.toString().trim()) {
                  return (
                    <div key={key} className="flex">
                      <span className="font-medium text-gray-300 w-1/3 capitalize">
                        {key.replace(/_/g, ' ')}:
                      </span>
                      <span className="text-white w-2/3 break-words">
                        {value}
                      </span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-black/30 px-6 py-4 rounded-b-2xl border-t border-yellow-500/10">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-200 font-medium shadow-lg shadow-yellow-500/30"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonDetailPopover;